import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import LangThemeToggle from "@/components/LangThemeToggle";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { lang } = useApp();
  const tr = t[lang];

  // Check if already logged in as admin
  useEffect(() => {
    base44.auth.me().then(me => {
      if (me && me.role === "admin") navigate("/admin/dashboard");
    }).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      await base44.auth.loginViaEmailPassword(username, password);
      const me = await base44.auth.me();
      if (me.role !== "admin") {
        await base44.auth.logout();
        setError(tr.noPermission);
        setLoading(false);
        return;
      }
      window.location.href = "/admin/dashboard";
    } catch {
      setError(tr.loginError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sidebar via-sidebar-accent to-primary flex items-center justify-center p-6" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Lang/Theme toggle top corner */}
      <div className="fixed top-4 left-4 z-50">
        <LangThemeToggle />
      </div>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png"
            alt={tr.appName}
            className="h-24 w-24 object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-black text-white">{tr.adminPanel}</h1>
          <p className="text-sidebar-foreground/70 text-sm mt-1">{tr.appName}</p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-bold text-foreground mb-6 text-center">{tr.login}</h2>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-foreground mb-2">
                {tr.email}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-secondary/20"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-foreground mb-2">
                {tr.password}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-secondary/20"
                required
              />
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-xl p-3 text-sm text-center font-medium">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-primary to-accent text-white font-black rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  {tr.loggingIn}
                </span>
              ) : `🔐 ${tr.login}`}
            </button>
          </form>
        </div>

        <p className="text-center text-sidebar-foreground/50 text-xs mt-6">
          {lang === "ar" ? "نظام المغسلة الذكية" : "Smart laundry system"} © 2026
        </p>
      </div>
    </div>
  );
}